import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { FormattedError } from '@app/common/Components/FormattedError'
import {
  AlertType,
  useNotifications
} from '@app/common/services/Notifications/NotificationsProvider'
import { useIntl } from '@app/intl/Intl'
import {
  CourtCaseFeeEntry,
  CourtCaseFeeTables,
  fetchFeeTables
} from '@app/modules/CourtCases/Components/CourtCaseInvoicingForm/Components/InvoicingFeeTablesContext/fetchFeeTables'

export interface InvoicingFeeTablesContextValues {
  loadingFeeTables: boolean
  feeTableKeys: string[]
  setCourtCaseFee: () => void
}

const InvoicingFeeTablesContext = createContext<InvoicingFeeTablesContextValues>(
  null as unknown as InvoicingFeeTablesContextValues
)

export const InvoicingFeeTablesProvider = ({ children }: { children?: React.ReactNode }) => {
  const { formatMessage } = useIntl()
  const { getValues, setValue } = useFormContext()
  const [loadingFeeTables, setLoadingFeeTables] = useState(true)
  const [feeTables, setFeeTables] = useState<CourtCaseFeeTables>({})
  const { pushNotification } = useNotifications()

  useEffect(() => {
    fetchFeeTables()
      .then((parsedFeeTables) => setFeeTables(parsedFeeTables))
      .finally(() => setLoadingFeeTables(false))
      .catch((error) =>
        pushNotification({
          type: AlertType.error,
          message: <FormattedError error={error} />
        })
      )
  }, [])

  const feeTableKeys = useMemo(() => {
    if (!feeTables) {
      return [] as string[]
    }

    return Object.keys(feeTables)
  }, [feeTables])

  const lookUpFee = ({
    feeTableForDateRange,
    disputeValue
  }: {
    feeTableForDateRange: CourtCaseFeeEntry[]
    disputeValue: number
  }) => {
    if (feeTableForDateRange.length === 0) {
      return null
    }

    for (const entry of feeTableForDateRange) {
      if (disputeValue <= entry.value) {
        return entry.fee
      }
    }

    return feeTableForDateRange[feeTableForDateRange.length - 1].fee
  }

  const setCourtCaseFee = useCallback(() => {
    const disputeValueRaw = getValues('DisputeValue') as unknown as string | number | undefined
    const dateRangeForFees = getValues('LegalPosition') || undefined

    const feeTableForDateRange = feeTables ? feeTables[dateRangeForFees] : undefined

    if (!disputeValueRaw || !dateRangeForFees) {
      setValue('CourtCaseFee', 0)
      return
    }

    if (!feeTableForDateRange) {
      pushNotification({
        type: AlertType.error,
        message: formatMessage({ id: 'CourtCases.Invoicing.Error.MissingFeeTable' })
      })

      setValue('CourtCaseFee', 0)

      return
    }

    const disputeValue =
      typeof disputeValueRaw === 'string' ? parseFloat(disputeValueRaw) : disputeValueRaw

    setValue('CourtCaseFee', lookUpFee({ feeTableForDateRange, disputeValue }))
  }, [getValues, setValue, feeTableKeys, lookUpFee])

  return (
    <InvoicingFeeTablesContext.Provider
      value={{
        loadingFeeTables,
        feeTableKeys,
        setCourtCaseFee
      }}
    >
      {children}
    </InvoicingFeeTablesContext.Provider>
  )
}

export const useInvoicingFeeTablesContext = () => useContext(InvoicingFeeTablesContext)
